<template>
    <div class="users-create-modal">
        <ModalContainer :title="$t('users.add_user')" identifier="users-create-modal" :closable="true">
            <Form class="form" @submit="save" :disabled="is_saving">
                <FormGroupTwo>
                    <FormInputText v-model="$v.user.name.$model" ref="name" identifier="name" :label="$t('users.name')"
                                   :disabled="is_saving" :has-error="$v.user.name.$error">
                        <template v-slot:errors>
                            <p v-if="!$v.user.name.required">
                                {{$t('validation.x_is_required',{x: $t('users.name')})}}
                            </p>
                        </template>
                    </FormInputText>
                    <FormInputText v-model="$v.user.email.$model" identifier="email" :label="$t('users.email')"
                                   :disabled="is_saving" :has-error="$v.user.email.$error" autocomplete="off">
                        <template v-slot:errors>
                            <p v-if="!$v.user.email.required">
                                {{$t('validation.x_is_required',{x: $t('users.email')})}}
                            </p>
                            <p v-else-if="!$v.user.email.email">
                                {{$t('auth.enter_valid_email')}}
                            </p>
                        </template>
                    </FormInputText>

                    <FormInputPassword v-model="$v.user.password.$model" identifier="new-password" :label="$t('users.password')"
                                       :disabled="is_saving" type="password" :has-error="$v.user.password.$error" autocomplete="new-password">
                        <template v-slot:errors>
                            <p v-if="!$v.user.password.required">
                                {{$t('validation.x_is_required',{x: $t('users.password')})}}
                            </p>
                        </template>
                    </FormInputPassword>

                    <FormInputSelect v-model="$v.user.job_roles.$model" identifier="job_role" :label="$t('users.job_roles')" :options="job_role_options"
                                     :disabled="is_saving || is_loading_job_roles" :has-error="$v.user.job_roles.$error" track-by="id"
                                     :display-custom-label="(row) => `${row.attributes.name}`" :multiple="true">
                        <template v-slot:errors>
                            <p v-if="!$v.user.job_roles.required">
                                {{$t('validation.x_are_required',{x: $t('users.job_roles')})}}
                            </p>
                        </template>
                    </FormInputSelect>
                </FormGroupTwo>

                <FormInputSelect v-model="$v.user.roles.$model" identifier="role" :label="$t('users.permissions')" :options="role_options"
                                 :disabled="is_saving || is_loading_roles" :has-error="$v.user.roles.$error" track-by="id"
                                 :display-custom-label="(row) => `${row.attributes.name}`" :multiple="true">
                    <template v-slot:errors>
                        <p v-if="!$v.user.roles.required">
                            {{$t('validation.x_are_required',{x: $t('users.permissions')})}}
                        </p>
                    </template>
                </FormInputSelect>

                <Button type="submit" className="--primary --small" :class="{spinner: is_saving}">
                    {{$t('save')}}
                </Button>
            </Form>
        </ModalContainer>
    </div>
</template>

<script>
    import ModalContainer from "../modal/ModalContainer";
    import Form from "../form/Form";
    import FormInputText from "../form/FormInputText";
    import FormGroupTwo from "../form/FormGroupTwo";
    import FormInputSelect from "../form/FormInputSelect";
    import {required, email} from 'vuelidate/lib/validators'
    import Button from "../Button";
    import FormInputPassword from "@/components/form/FormInputPassword";

    export default {
        name: "UsersCreateModal",
        components: {FormInputPassword, Button, FormInputSelect, FormGroupTwo, FormInputText, Form, ModalContainer},
        data() {
            return {
                user: {
                    name: null,
                    email: null,
                    password: null,
                    roles: [],
                    job_roles: [],
                },
                role_options: [],
                job_role_options: [],
                is_loading_roles: false,
                is_loading_job_roles: false,
                is_saving: false,
            }
        },
        validations: {
            user: {
                name: {required},
                email: {required, email},
                password: {required},
                roles: {required},
                job_roles: {},
            }
        },
        methods: {
            close(status) {
                this.$modal.hide('users-create-modal', status);
            },
            save() {
                this.$v.user.$touch();
                if (this.$v.user.$anyError || this.is_saving)
                    return;

                this.is_saving = true;

                let payload = {
                    name: this.user.name,
                    email: this.user.email,
                    password: this.user.password,
                    roles: this.user.roles.map(role => role.id),
                }

                if(this.user.job_roles.length)
                    payload = {...payload, job_roles: this.user.job_roles.map(job_role => job_role.id)};

                this.$axios.post(`users`, payload).then(({data}) => {
                    this.$notify({
                        text: this.$t('users.success_created'),
                        type: 'success',
                    });

                    this.is_saving = false;
                    this.close(true);
                }).catch(e => {
                    this.is_saving = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data.errors, this.$t('users.error_create')),
                        type: 'error',
                    });
                });
            },
            getJobRoleOptions() {
                this.is_loading_job_roles = true;

                this.$axios.get('jobRoles/list')
                    .then(({data}) => {
                        this.job_role_options = data.data;
                        this.is_loading_job_roles = false;
                    })
                    .catch(e => {
                        this.is_loading_job_roles = false;

                        this.$notify({
                            title: this.$t('error'),
                            text: this.$larerror(e.response.data, this.$t('job_roles.error_retrieve')),
                            type: 'error',
                        });
                    });
            },
            getRoleOptions() {
                this.is_loading_roles = true;

                this.$axios.get('roles/list')
                    .then(({data}) => {
                        this.role_options = data.data;
                        this.is_loading_roles = false;
                    })
                    .catch(e => {
                        this.is_loading_roles = false;

                        this.$notify({
                            title: this.$t('error'),
                            text: this.$larerror(e.response.data, this.$t('roles.error_retrieve')),
                            type: 'error',
                        });
                    });
            },
        },
        mounted() {
            this.getRoleOptions();
            this.getJobRoleOptions();

            this.$refs.name.$el.children[1].focus();
        }
    }
</script>
